import React, { PropsWithChildren } from 'react';
import { Package, Truck, PackageOpen, MessageSquareText } from 'lucide-react';
import clsx from 'clsx';
import OrderStatusChip from './OrderStatusChip';
import DialogTitle from '../DialogTitle';
import { transformDataAndTime } from '../../../utils/helperFunction/transformDate';
import { formatPrice } from '../../../utils/formatPrice';
import { orderType } from '../../../types/order/orderType';
import { IonAvatar } from '@ionic/react';
import images from '../../../assets/images';
import { IonicButton } from 'ionic-ui';
import useChatNavigator from '../../../hooks/useChatNavigator';

const InfoCard: React.FC<
  PropsWithChildren & { title: string; className?: string }
> = ({ title, children, className = '' }) => {
  return (
    <div className={`space-y-1 truncate ${className}`}>
      <small className="medium-emphasis-text capitalize block select-none">
        {title}
      </small>
      <div className="font-semibold">{children}</div>
    </div>
  );
};

export default function OrderDetailsDialogue(props: orderType) {
  const blueSurfaceStyle = clsx(
    'rounded-md space-y-4 p-6 bg-customs-light-blue-foreground'
  );

  const { goToConversation } = useChatNavigator();
  const handleCustomerMessaging = () => {
    return goToConversation({
      title: `order-${props.reference}`,
      type: 'direct',
      meta_data: {
        participants: [
          {
            role_id: props.customer.role_id,
            user_id: props.customer.user_id,
          },
        ],
      },
    });
  };

  return (
    <div className="space-y-4 dialog-content">
      <DialogTitle>
        <span>📦</span>
        <span>Order Details</span>
      </DialogTitle>

      {/* ORDER SUMMARY */}
      <div className="space-y-2">
        <h6 className="high-emphasis-text font-medium">Order Summary</h6>
        <div className={blueSurfaceStyle}>
          <div className="grid min-[420px]:grid-cols-2 gap-4 w-full">
            <InfoCard title="Order ID">
              <p className="font-semibold truncate" title={props.reference}>
                {props.reference}
              </p>
            </InfoCard>
            <InfoCard title="Order Date">
              <p
                className="font-semibold truncate"
                title={transformDataAndTime(props.created_at || '')}
              >
                {transformDataAndTime(props.created_at || '')}
              </p>
            </InfoCard>
            <InfoCard title="Status">
              <OrderStatusChip status={props.status} />
            </InfoCard>
            <InfoCard title="Total Amount">
              <p className="font-semibold truncate">
                {formatPrice(Number(props.total_price))}
              </p>
            </InfoCard>
          </div>

          {/* DIVIDER */}
          <span className="w-full h-[1px] bg-border block" />

          {/* PRODUCTS ORDERED  */}
          <div className="space-y-4">
            <p className="font-semibold high-emphasis-text">Product Ordered</p>
            <div className="p-4 bg-white rounded-md flex justify-between flex-wrap gap-4">
              <div className="flex flex-1 gap-4">
                <IonAvatar className="w-11 min-w-11 min-h-11 h-11">
                  <img
                    src={props.default_image_url || images.placeholder}
                    className="w-11 min-w-11 min-h-11 h-11 object-cover object-center"
                  />
                </IonAvatar>
                <div className="space-y-2">
                  <p
                    className="font-bold w-[70%] high-emphasis-text line-clamp-2"
                    title={props.product_name}
                  >
                    {props.product_name}
                  </p>
                  <small className="medium-emphasis-text space-x-1">
                    <span>Quantity: {props.quantity}</span>
                    <span>x</span>
                    <span>{formatPrice(Number(props.unit_price))} each</span>
                  </small>
                </div>
              </div>
              <h6 className="font-semibold ml-auto text-blue-950">
                {formatPrice(Number(props.total_price))}
              </h6>
            </div>
          </div>
        </div>
      </div>

      {/* BUYER INFORMATION */}
      <div className="space-y-2">
        <h6 className="high-emphasis-text font-medium">Buyer Information</h6>
        <div className={blueSurfaceStyle}>
          <div className="grid min-[420px]:grid-cols-2 gap-4 w-full">
            <InfoCard title="Customer Name">
              <p
                className="font-medium truncate"
                title={`${props.customer.last_name} ${props.customer.first_name}`}
              >
                {`${props.customer.last_name} ${props.customer.first_name}`}
              </p>
            </InfoCard>
            <InfoCard title="Customer Email">
              <p
                className="font-medium truncate"
                title={props.customer.email || 'Not Provided'}
              >
                {props.customer.email || 'Not Provided'}
              </p>
            </InfoCard>
            <InfoCard title="Customer Phone Number">
              <p
                className="font-medium truncate"
                title={props?.customer.phone ?? 'Not Provided'}
              >
                {props?.customer.phone ?? 'Not Provided'}
              </p>
            </InfoCard>
            {/* <InfoCard title="Payment Method">
              <p className="font-medium truncate" title={paymentMethod}>
                {paymentMethod}
              </p>
            </InfoCard> */}
          </div>
          <IonicButton
            variant="outline"
            className="button-with-icon w-full"
            onClick={handleCustomerMessaging}
          >
            <span>
              <MessageSquareText className="size-4" />
            </span>
            <span>Chat with {props.customer.last_name}</span>
          </IonicButton>
        </div>
      </div>

      {/* DELIVER ADDRESS */}
      <div className="space-y-2">
        <h6 className="high-emphasis-text font-medium">Delivery Address</h6>
        <div className="rounded-md space-y-4 px-6 py-4 border border-border">
          <div className="space-y-4">
            <InfoCard title="Home Address">
              <p
                className="font-medium truncate"
                title={
                  props.delivery_address.address_line_1 ||
                  props.delivery_address.address_line_1 ||
                  'Not Provided'
                }
              >
                {props.delivery_address.address_line_1 ||
                  props.delivery_address.address_line_1 ||
                  'Not Provided'}
              </p>
            </InfoCard>

            <div className=" flex items-start justify-between gap-6 flex-wrap">
              <InfoCard title="City" className="flex-1 min-w-fit">
                <p
                  className="font-medium"
                  title={props.delivery_address.city || 'Not Provided'}
                >
                  {props.delivery_address.city || 'Not Provided'}
                </p>
              </InfoCard>
              <InfoCard title="State" className="flex-1 min-w-fit">
                <p
                  className="font-medium"
                  title={props.delivery_address.state || 'Not Provided'}
                >
                  {props.delivery_address.state || 'Not Provided'}
                </p>
              </InfoCard>
              <InfoCard title="Country" className="flex-1 min-w-fit">
                <p
                  className="font-medium"
                  title={props.delivery_address.country || 'Not Provided'}
                >
                  {props.delivery_address.country || 'Not Provided'}
                </p>
              </InfoCard>
            </div>
          </div>
        </div>
      </div>

      {/* CALL-TO-ACTION BUTTONS */}
      <div className="flex items-center flex-wrap gap-2 w-full">
        {/* MARK AS PACKAGED BUTTON */}
        <div
          className={`${props.status == 'pending' ? 'text-primary bg-customs-light-blue-foreground' : 'low-emphasis-text bg-transparent'} font-medium text-nowrap px-6 py-2 flex-1 flex items-center gap-2 justify-center rounded-md`}
        >
          <PackageOpen className="size-5" />
          <p>Order Pending</p>
        </div>

        {/* MARK AS PACKAGED BUTTON */}
        <div
          className={`${props.status == 'shipped' ? 'text-primary bg-customs-light-blue-foreground' : 'low-emphasis-text bg-transparent'} font-medium text-nowrap px-6 py-2 flex-1 flex items-center gap-2 justify-center rounded-md border border-border`}
        >
          <Truck className="size-5" />
          <p>Out for delivery</p>
        </div>

        {/* MARK AS PACKAGED BUTTON */}
        <div
          className={`${props.status == 'delivered' ? 'text-primary bg-customs-light-blue-foreground' : 'low-emphasis-text bg-transparent'} font-medium text-nowrap px-6 py-2 flex-1 flex items-center gap-2 justify-center rounded-md border border-border`}
        >
          <Package className="size-5" />
          <p>Marked as delivered</p>
        </div>
      </div>
    </div>
  );
}
